import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DomainRoundedIcon, KeyboardArrowRightIcon } from "@l2r-front/l2r-icons"
import { useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import { useNetworksContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"

import { MobileSubNetworkSelector } from "../MobileSubNetworkSelector"

import * as Styled from "./MobileNetworkSelector.styled"

export function MobileNetworkSelector(props) {

    const {
        onClose,
    } = props

    const [parentNetwork, setParentNetwork] = useState(null)
    const [subNetworksDisplayed, setSubNetworksDisplayed] = useState(null)

    const [networksState, networksDispatch] = useNetworksContext()
    const { allNetworks, selectedNetwork, selectedSubNetworkSlugs } = networksState
    const { setImageFeatureClickedPosition } = useImageCollectionDispatchContext()
    const { swapToNetwork } = networksDispatch
    const { t } = useTranslation(I18N_NAMESPACE)

    const changeSelectedNetworkSlug = useCallback((newNetwork) => {
        if (newNetwork.children.length > 1) {
            setParentNetwork(newNetwork)
            if (newNetwork.slug === selectedNetwork.slug) {
                setSubNetworksDisplayed(selectedSubNetworkSlugs)
            } else {
                setSubNetworksDisplayed(newNetwork.children.map(subnetwork => subnetwork.slug))
            }
        } else if (newNetwork.slug !== selectedNetwork.slug) {
            swapToNetwork(newNetwork.slug)
            setImageFeatureClickedPosition()

            onClose()
        }
    }, [
        selectedNetwork,
        selectedSubNetworkSlugs,
        setImageFeatureClickedPosition,
        swapToNetwork,
        onClose,
    ])

    const parentNetworks = useMemo(() => {
        if (!allNetworks) {
            return []
        }
        return allNetworks.filter(n => !n.parent)
    }, [allNetworks])

    const networksList = useMemo(() => {
        if (!parentNetworks) {
            return null
        }

        return parentNetworks.map(network => {
            const isSelected = selectedNetwork?.slug === network.slug
            return <Styled.ListItemButton
                selected={isSelected}
                onClick={() => changeSelectedNetworkSlug(network)}
                key={network.slug}
            >
                <DomainRoundedIcon color={isSelected ? "primary" : "default"} />
                <Styled.NetworkLabel variant="Desktop & Mobile/Body/Regular">{network.children.length === 1 ? network.children[0].name : network.name}</Styled.NetworkLabel>
                {network.children.length > 1 && <KeyboardArrowRightIcon />}
            </Styled.ListItemButton>

        })
    }, [parentNetworks, selectedNetwork, changeSelectedNetworkSlug])

    const handleSubNetworkSelectorClose = useCallback((reason) => {
        setParentNetwork(null)
        setSubNetworksDisplayed(null)

        if (reason === "apply") {
            onClose()
        }
    }, [onClose])

    const handleCloseBottomSheet = useCallback(() => {
        setParentNetwork(null)
        setSubNetworksDisplayed(null)
    }, [])

    if (!networksList) {
        return <Styled.Text variant="Desktop & Mobile/Body/Regular">
            {t(I18N_NAMESPACE, "containers.networkSelector.noNetwork")}
        </Styled.Text>
    }

    return (
        <>
            <Styled.List>
                {networksList}
            </Styled.List>
            {!!parentNetwork &&
                <Styled.BottomSheet displayPuller open={!!parentNetwork?.children?.length} onClose={handleCloseBottomSheet}>
                    <MobileSubNetworkSelector selectedNetwork={parentNetwork} selectedSubNetworkSlugs={subNetworksDisplayed} onClose={handleSubNetworkSelectorClose} />
                </Styled.BottomSheet>
            }
        </>
    )
}

MobileNetworkSelector.propTypes = {
    onClose: PropTypes.func.isRequired,
}
